import type { IUseCase } from 'shared-kernel';
import type { IAuthRepository, ITokenService, IStorageService } from '../../../domain/ports';
import type { LoginCredentials, LoginResult } from '../../dto/dtos';
import { User } from '../../../domain/entities';

export interface LoginInput {
  credentials: LoginCredentials;
}

export interface LoginOutput {
  user: User;
  requires2FA: boolean;
  requires2FAConfiguration: boolean;
  twoFactorSessionData: {
    idUtilisateur: string;
    sessionId: string;
    methode2FA: 'EMAIL' | 'APPLICATION';
  } | null;
}

export class LoginUseCase implements IUseCase<LoginInput, LoginOutput> {
  constructor(
    private readonly authRepository: IAuthRepository,
    private readonly tokenService: ITokenService,
    private readonly storageService: IStorageService,
  ) {}

  async execute(input: LoginInput): Promise<LoginOutput> {
    const response = await this.authRepository.login(input.credentials);

    if (!response.succes || !response.data) {
      throw new Error(response.message || 'Echec de la connexion');
    }

    const loginResult: LoginResult = response.data;

    // 2FA requis → session temporaire
    if (loginResult.deuxiemeFacteur?.requis) {
      const user = User.fromLoginResult(loginResult.utilisateur);

      return {
        user,
        requires2FA: true,
        requires2FAConfiguration: !loginResult.deuxiemeFacteur.configure,
        twoFactorSessionData: {
          idUtilisateur: loginResult.utilisateur.idUtilisateur,
          sessionId: loginResult.session.idSession,
          methode2FA: loginResult.deuxiemeFacteur.methode,
        },
      };
    }

    // Connexion directe sans 2FA
    const { accessToken } = loginResult.tokens;
    if (!accessToken) {
      throw new Error('Tokens manquants dans la reponse de connexion');
    }

    this.tokenService.setAccessToken(accessToken);

    const user = User.fromLoginResult(loginResult.utilisateur);
    this.storageService.setUser(user.toProfile());

    return {
      user,
      requires2FA: false,
      requires2FAConfiguration: false,
      twoFactorSessionData: null,
    };
  }
}
